import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import { UploadScanStatus } from 'src/types';
import { FileRow } from './FileRow';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { ChangeEvent, RefObject, Ref } from 'react';

export const ALLOWED_EXTENSIONS = [
  '.docx',
  '.doc',
  '.pptx',
  '.ppt',
  '.xlsx',
  '.xls',
  '.pdf',
];
export const ALLOWED_ACCEPT = ALLOWED_EXTENSIONS.join(',');
export const MAX_FILE_SIZE_MB = 25;
export const MAX_TOTAL_SIZE_MB = 100;
export const MAX_TITLE_LENGTH = 140;
export const MAX_DESC_LENGTH = 140;

export const REMINDER_FREQUENCY_OPTIONS = [
  { label: 'Daily', value: 1 },
  { label: 'Weekly', value: 7 },
  { label: 'Every 2 weeks', value: 14 },
  { label: 'Monthly', value: 30 },
];

export const REMINDER_MAX_COUNT_OPTIONS = [1, 2, 3, 5, 10];

export const STEPS = ['Details', 'File upload', 'Additional signatories'];

export interface UploadedFile {
  file: File;
  uploadId: string;
  scanStatus: UploadScanStatus;
}

export interface SelectedUser {
  id: number;
  name: string;
  role?: string | null;
  department?: string | null;
}

interface SearchUser {
  id: number;
  name?: string | null;
  role?: string | null;
  department?: string | null;
}

interface Step1Props {
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  deadline: string;
  setDeadline: (v: string) => void;
  reminderFrequency: number;
  setReminderFrequency: (v: number) => void;
  reminderMaxCount: number | null;
  setReminderMaxCount: (v: number | null) => void;
  sp: LensTheme['tokens']['semantic']['spacing']['desktop']['gap'];
}

export const Step1 = ({
  title,
  setTitle,
  description,
  setDescription,
  deadline,
  setDeadline,
  reminderFrequency,
  setReminderFrequency,
  reminderMaxCount,
  setReminderMaxCount,
  sp,
}: Step1Props) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp[2].value }}>
    <TextField
      label="Plan title *"
      value={title}
      onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE_LENGTH))}
      fullWidth
      inputProps={{ maxLength: MAX_TITLE_LENGTH }}
      helperText={`${title.length}/${MAX_TITLE_LENGTH}`}
      data-testid="plan-title-input"
    />
    <TextField
      label="Plan description *"
      value={description}
      onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC_LENGTH))}
      fullWidth
      multiline
      rows={3}
      inputProps={{ maxLength: MAX_DESC_LENGTH }}
      helperText={`${description.length}/${MAX_DESC_LENGTH}`}
      data-testid="plan-description-input"
    />
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DesktopDatePicker
        label="Mitigation deadline *"
        value={deadline ? dayjs(deadline) : null}
        onChange={(date) =>
          setDeadline(date?.isValid() ? date.format('YYYY-MM-DD') : '')
        }
        minDate={dayjs()}
        slotProps={{
          textField: {
            fullWidth: true,
            inputProps: { 'data-testid': 'plan-deadline-input' },
          },
        }}
      />
    </LocalizationProvider>
    <Box>
      <Typography
        sx={{
          mb: sp[1].value,
          display: 'block',
          typography: 'labelSm',
          fontWeight: 600,
        }}
      >
        Reminder settings
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: sp[2].value,
        }}
      >
        <TextField
          label="Frequency *"
          select
          value={reminderFrequency}
          onChange={(e) => setReminderFrequency(Number(e.target.value))}
          fullWidth
          data-testid="plan-reminder-frequency-input"
        >
          <MenuItem value={0} disabled>
            Choose an option
          </MenuItem>
          {REMINDER_FREQUENCY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Maximum count *"
          select
          value={reminderMaxCount ?? ''}
          onChange={(e) =>
            setReminderMaxCount(e.target.value ? Number(e.target.value) : null)
          }
          fullWidth
          data-testid="plan-reminder-max-count-input"
        >
          <MenuItem value="" disabled>
            Choose an option
          </MenuItem>
          {REMINDER_MAX_COUNT_OPTIONS.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
  </Box>
);

export interface ExistingUpload {
  uploadId: string;
  uploadOrder: number;
  fileExtension?: string | null;
  scanStatus?: string | null;
}

interface Step2Props {
  uploadedFiles: UploadedFile[];
  existingUploads?: ExistingUpload[];
  fileError: string;
  uploading: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (uploadId: string) => void;
  onRemoveExistingUpload?: (uploadId: string) => void;
  color: LensTheme['tokens']['semantic']['color'];
  sp: LensTheme['tokens']['semantic']['spacing']['desktop']['gap'];
}

export const Step2 = ({
  uploadedFiles,
  existingUploads = [],
  fileError,
  uploading,
  fileInputRef,
  onFileChange,
  onRemoveFile,
  onRemoveExistingUpload,
  color,
  sp,
}: Step2Props) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp[2].value }}>
    <Box
      role="button"
      tabIndex={uploading ? -1 : 0}
      sx={{
        border: `2px dashed ${color.ui.divider.default.value}`,
        borderRadius: '4px',
        padding: '48px 24px',
        textAlign: 'center',
        cursor: uploading ? 'default' : 'pointer',
        backgroundColor: color.surface.variant.value,
      }}
      onClick={() => !uploading && fileInputRef.current?.click()}
      onKeyDown={(e) => {
        if (!uploading && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          fileInputRef.current?.click();
        }
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (!uploading) {
          const files = Array.from(e.dataTransfer.files);
          const syntheticEvent = {
            target: { files },
          } as unknown as ChangeEvent<HTMLInputElement>;
          onFileChange(syntheticEvent);
        }
      }}
      data-testid="file-drop-zone"
    >
      <input
        ref={fileInputRef as Ref<HTMLInputElement>}
        type="file"
        accept={ALLOWED_ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={onFileChange}
        data-testid="file-input"
      />
      {uploading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <BackupOutlinedIcon sx={{ fontSize: 32, mb: '12px' }} />
          <Typography variant="body1" fontWeight={600}>
            Drag files here or
          </Typography>
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{ textDecoration: 'underline', mb: '16px' }}
          >
            select files to upload
          </Typography>
          <Typography variant="body2" color={color.type.muted.value}>
            Formats: {ALLOWED_EXTENSIONS.join(', ')}
          </Typography>
          <Typography variant="body2" color={color.type.muted.value}>
            Max. file size: {MAX_FILE_SIZE_MB} MB / {MAX_TOTAL_SIZE_MB} MB in
            total for the plan
          </Typography>
        </>
      )}
    </Box>

    {fileError && (
      <Typography
        variant="caption"
        color={color.action.destructive.default.value}
        data-testid="file-error"
      >
        {fileError}
      </Typography>
    )}

    {existingUploads.length > 0 && onRemoveExistingUpload && (
      <Box
        sx={{
          border: `1px solid ${color.ui.divider.default.value}`,
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        {existingUploads.map((u, index) => (
          <FileRow
            key={u.uploadId}
            uploadId={u.uploadId}
            label={
              u.fileExtension
                ? `Attachment ${u.uploadOrder + 1} (.${u.fileExtension})`
                : `Attachment ${u.uploadOrder + 1}`
            }
            scanStatus={u.scanStatus}
            onRemove={onRemoveExistingUpload}
            isFirst={index === 0}
            color={color}
            removeTestId={`remove-existing-file-${u.uploadId}`}
          />
        ))}
      </Box>
    )}

    {uploadedFiles.length > 0 && (
      <Box
        sx={{
          border: `1px solid ${color.ui.divider.default.value}`,
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        {uploadedFiles.map(({ file, uploadId, scanStatus }, index) => (
          <FileRow
            key={uploadId}
            uploadId={uploadId}
            label={file.name}
            scanStatus={scanStatus}
            onRemove={onRemoveFile}
            isFirst={index === 0}
            color={color}
            removeTestId={`remove-file-${uploadId}`}
          />
        ))}
      </Box>
    )}
  </Box>
);

interface Step3Props {
  userSearch: string;
  setUserSearch: (v: string) => void;
  users: SearchUser[];
  usersLoading: boolean;
  usersError: boolean;
  caseQueryError?: boolean;
  selectedUsers: SelectedUser[];
  toggleUser: (user: SelectedUser) => void;
  color: LensTheme['tokens']['semantic']['color'];
  sp: LensTheme['tokens']['semantic']['spacing']['desktop']['gap'];
}

export const Step3 = ({
  userSearch,
  setUserSearch,
  users,
  usersLoading,
  usersError,
  caseQueryError,
  selectedUsers,
  toggleUser,
  color,
  sp,
}: Step3Props) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: sp[2].value,
      flex: 1,
      minHeight: 0,
    }}
  >
    <TextField
      label="Search employees"
      value={userSearch}
      onChange={(e) => setUserSearch(e.target.value)}
      fullWidth
      data-testid="user-search-input"
    />

    {caseQueryError && (
      <Alert severity="error" sx={{ mt: sp[1].value }}>
        Failed to load case details. The reviewer list may include the reporter.
        Please contact support.
      </Alert>
    )}

    <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
      {usersLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '16px' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '40px 1fr 1fr 1fr',
              gap: '8px',
              py: '8px',
              borderBottom: `1px solid ${color.ui.divider.default.value}`,
            }}
          >
            <Box />
            <Typography
              variant="caption"
              color={color.type.muted.value}
              fontWeight={600}
            >
              Employee name
            </Typography>
            <Typography
              variant="caption"
              color={color.type.muted.value}
              fontWeight={600}
            >
              Role
            </Typography>
            <Typography
              variant="caption"
              color={color.type.muted.value}
              fontWeight={600}
            >
              Department
            </Typography>
          </Box>
          {users.map((user) => {
            const name = user.name ?? `User ${user.id}`;
            const initials = name
              .split(' ')
              .slice(0, 2)
              .map((n) => n[0])
              .join('');
            const isSelected = selectedUsers.some((u) => u.id === user.id);
            return (
              <Box
                key={user.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr 1fr 1fr',
                  gap: '8px',
                  py: '8px',
                  alignItems: 'center',
                  borderBottom: `1px solid ${color.ui.divider.default.value}`,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: color.surface.variant.value },
                }}
                onClick={() =>
                  toggleUser({
                    id: user.id,
                    name,
                    role: user.role,
                    department: user.department,
                  })
                }
                data-testid={`user-row-${user.id}`}
              >
                <Checkbox
                  checked={isSelected}
                  size="small"
                  onChange={() =>
                    toggleUser({
                      id: user.id,
                      name,
                      role: user.role,
                      department: user.department,
                    })
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '11px' }}>
                    {initials}
                  </Avatar>
                  <Typography variant="body2">{name}</Typography>
                </Box>
                <Typography variant="body2" color={color.type.muted.value}>
                  {user.role ?? '—'}
                </Typography>
                <Typography variant="body2" color={color.type.muted.value}>
                  {user.department ?? '—'}
                </Typography>
              </Box>
            );
          })}
          {usersError && (
            <Alert severity="error" sx={{ my: '8px' }}>
              Failed to load employees. Please try again.
            </Alert>
          )}
          {users.length === 0 && !usersLoading && !usersError && (
            <Typography
              variant="body2"
              color={color.type.muted.value}
              sx={{ py: '16px', textAlign: 'center' }}
            >
              No employees found
            </Typography>
          )}
        </Box>
      )}
    </Box>

    {selectedUsers.length > 0 && (
      <Box sx={{ flexShrink: 0, pt: sp[1].value }}>
        <Typography
          variant="caption"
          color={color.type.muted.value}
          fontWeight={600}
          sx={{ display: 'block', mb: sp[1].value }}
        >
          Selected approvers
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {selectedUsers.map((u) => (
            <Chip
              key={u.id}
              label={u.name}
              size="small"
              onDelete={() => toggleUser(u)}
              data-testid={`selected-user-chip-${u.id}`}
            />
          ))}
        </Box>
      </Box>
    )}
  </Box>
);
